import type { TFunction } from 'i18next';
import { useCallback } from 'react';
import useU2000AxiosAuthenticated from '../../../../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../../../../u2000Core/axios/U2000HandleApiError';
import {
  doCreateChildLotChainData,
  luHandleAppiError,
  u3141ParseChildLotChainCreateResponse,
  type U3141ChildLotChain,
} from '../../../../U3141Service';
import { NS } from '../../../../U3141';
import {
  u3141BuildConcordancePlacements,
  u3141MapChildLotChainToCreatePayload,
  type U3141ChildLotChainCreatePayload,
} from '../../../../tools/u3141ChildLotChainCreateMapper';

export interface U3141CreateChildLotFromGraphParams {
  masterLotChainId: string;
  childLotChain: U3141ChildLotChain;
  selectedConcordanceEntries: string[];
  nodePosition: { x: number; y: number };
}

export interface U3141UseCreateChildLotChainFromGraphHandlersParams {
  t: TFunction;
  toast: { success: (text: string, title?: string) => void; error: (text: string, title?: string) => void };
  onChildCreated: (child: U3141ChildLotChain) => void;
  /** BL-0697 — layout hiérarchique post-create sur lotChainGraph. */
  applyLotChainGraphSnapshotFromGraphCreate: (graph: U3141ChildLotChain[]) => void;
  refetchMasterLotChain?: () => Promise<void>;
  /** Si lotChainGraph absent de la réponse create, recharge le graphe ouvert. */
  reloadLotChainGraphIfDialogOpen?: (masterLotChainId: string) => Promise<void>;
}

export function useU3141CreateChildLotChainFromGraphHandlers(params: U3141UseCreateChildLotChainFromGraphHandlersParams) {
  const {
    t,
    toast,
    onChildCreated,
    applyLotChainGraphSnapshotFromGraphCreate,
    refetchMasterLotChain,
    reloadLotChainGraphIfDialogOpen,
  } = params;
  const axios = useU2000AxiosAuthenticated();

  const handleApiError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((errItem) => toast.error(errItem.text, errItem.title));
      }
      return errorResult;
    },
    [t, toast],
  );

  const createFromGraph = useCallback(
    async (input: U3141CreateChildLotFromGraphParams): Promise<boolean> => {
      const { masterLotChainId, childLotChain, selectedConcordanceEntries, nodePosition } = input;
      const concordancePlacements = u3141BuildConcordancePlacements(selectedConcordanceEntries, nodePosition);

      try {
        const payload: U3141ChildLotChainCreatePayload = u3141MapChildLotChainToCreatePayload(childLotChain, {
          selectedConcordanceEntries,
          nodePosition,
          concordancePlacements,
        });
        const createRes = await doCreateChildLotChainData(axios, masterLotChainId, payload);
        const parsed = u3141ParseChildLotChainCreateResponse(createRes.data);
        if (!parsed?.id) {
          toast.error(t('errAddLotFromGraphLotNotFound', { ns: NS }), t('errorTitle', { ns: NS }));
          return false;
        }
        const { lotChainGraph, ...created } = parsed;
        onChildCreated(created);
        if (lotChainGraph.length > 0) {
          applyLotChainGraphSnapshotFromGraphCreate(lotChainGraph);
        } else {
          await reloadLotChainGraphIfDialogOpen?.(masterLotChainId);
        }
        await refetchMasterLotChain?.();
        toast.success(t('msgAddLotFromGraphSaved', { ns: NS }), t('successTitle', { ns: NS }));
        return true;
      } catch (error) {
        handleApiError(error);
        return false;
      }
    },
    [
      axios,
      t,
      toast,
      onChildCreated,
      applyLotChainGraphSnapshotFromGraphCreate,
      refetchMasterLotChain,
      reloadLotChainGraphIfDialogOpen,
      handleApiError,
    ],
  );

  return { createFromGraph };
}
